import AjaxOptions from '@ember-data/adapter';
import JSONAPIAdapter from '@ember-data/adapter/json-api';
import type Router from '@ember/routing/router';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import ENV from 'donor-elf-web/config/environment';
import type CacheService from 'donor-elf-web/services/cache-service';
import type FundsService from 'donor-elf-web/services/funds-service';
import type WebVersion from 'donor-elf-web/services/web-version';
import type SessionService from 'ember-simple-auth/services/session';
import ExpiredFundTokenError from './expired-fund-token-error';
import moment from 'moment';

export default class ApplicationAdapter extends JSONAPIAdapter {
  @service declare session: SessionService;
  @service declare cacheService: CacheService;
  @service declare coachingService: any;
  @service declare fundsService: FundsService;
  @service declare router: Router;
  @service declare webVersion: WebVersion;

  lastExpiredFundTokenError: any = null;

  constructor() {
    super(...arguments);

    // Hack for now. needed for cookies to be sent from localhost:4200 to localhost:3000
    if (ENV.environment === 'development') {
      this.ajaxOptions = (
        url: string,
        method: string,
        options?: AjaxOptions,
      ): object => {
        options = Object.assign({}, options, { withCredentials: true });
        return super.ajaxOptions(url, method, options);
      };
    }
  }

  get headers() {
    let headers: Record<string, string> = {};

    if (this.session.isAuthenticated) {
      const { access_token } = this.session.data?.authenticated;

      if (!access_token) {
        console.error(
          'Session is authenticated but no authenticated data found.',
        );
      }

      headers['Authorization'] = `Bearer ${access_token}`;
    }

    headers['FundId'] = this.fundsService.selectedFund?.id || '';
    headers['Fund'] = this.fundsService.selectedFundToken;

    const coachingToken = this.coachingService.selectedIndividualToken;
    if (coachingToken) {
      headers['CoachingIndividual'] = coachingToken;
    }

    return headers;
  }

  get host() {
    return ENV.apiUrl;
  }

  get namespace() {
    return 'json-api/v1';
  }

  authorize(xhr: XMLHttpRequest) {
    const { access_token } = this.session.data.authenticated;

    if (isPresent(access_token)) {
      xhr.setRequestHeader('Authorization', `Bearer ${access_token}`);
    }
  }

  override handleResponse(
    status: number,
    headers: Record<string, string>,
    payload: any,
    requestData: {},
  ): any {
    if (
      headers['fund-token'] &&
      headers['fund-token'] !== this.fundsService.selectedFundToken
    ) {
      this.fundsService.selectedFundToken = headers['fund-token'];
    }

    if (status === 401) {
      if (payload.errors === 'expired_fund_token') {
        this.fundsService.selectedFundToken = '';

        // prevent infinite loop for ExpiredFundTokenError
        // confirm the last error happened more than a minute ago
        const lastExpiredFundTokenError = this.lastExpiredFundTokenError;

        if (
          isEmpty(lastExpiredFundTokenError) ||
          lastExpiredFundTokenError < moment().subtract(1, 'minutes')
        ) {
          this.lastExpiredFundTokenError = moment();
          return new ExpiredFundTokenError();
        }
      } else {
        this.router.transitionTo('logout');
        return;
      }
    }

    const { 'x-web-version': webVersion } = headers;
    if (webVersion) {
      this.webVersion.setCurrentVersion(webVersion);
    }

    return super.handleResponse(status, headers, payload, requestData);
  }

  // defaults namespace to `user` unless the type contains `/`
  override pathForType(type: any) {
    let folder = 'user';
    let folderType = type;

    if (type.indexOf('/') > 0) {
      const splitType = type.split('/');
      folder = splitType[0];
      folderType = splitType[1];
    }

    return super.pathForType(`${folder}/${folderType}`);
  }

  // always force `findAll` to not resolve until the data comes back from the server
  // otherwise it will resolve immediately if findAll was previously called on the model
  override shouldReloadAll() {
    return true;
  }

  override shouldReloadRecord() {
    return true;
  }
}
